import { CalendarTodayRounded, ExpandMoreOutlined } from "@mui/icons-material";
import {
  alpha,
  Box,
  CircularProgress,
  MenuItem,
  Select,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { subDays } from "date-fns";
import moment from "moment";
import React, { useEffect, useState } from "react";
import ReactJson from "react-json-view";
import { API_SERVICE_BACKEND, DEFAULT_DAYS_ON_CHART } from "src/config";
import { formatToLocalDate } from "src/utils/format-to-local-date";
import DateTimeRangePicker from "./dateTimeRangePicker";
import axios from "axios";

const MailboxLogs = ({ isDrawerOpen, acc }) => {
  const theme = useTheme();
  const SORTING_OPTIONS = [
    { label: "Ascending", value: "asc" },
    { label: "Descending", value: "desc" },
  ];
  const logTypes = [
    { label: "Last Processed", value: "lastProcessed" },
    { label: "Discarded", value: "discarded" },
    { label: "Send Error", value: "sendError" },
    { label: "Work Error", value: "workError" },
  ];
  const toUTCString = (date) => date.toISOString();
  const [dateRange, setDateRange] = useState({
    from: subDays(Date.now(), DEFAULT_DAYS_ON_CHART),
    to: new Date(Date.now()),
  });

  const [selectedDateRange, setSelectedDateRange] = useState({
    from: toUTCString(dateRange?.from),
    to: toUTCString(dateRange?.to),
  });

  const [mailBox, setMailBox] = useState([]);
  const [validLogTypes, setValidLogTypes] = useState(logTypes);
  const [type, setType] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");

  // handle sort order
  const handleSortOrderChange = (e) => {
    setSortOrder(e.target.value);
  };

  // const convertToUserTimezone = (date) => {
  //     return moment(date).tz(selectedTimezone).utc().format();
  // };

  const onDateRangeChange = (field, value) => {
    setDateRange((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleTypeChange = (e) => {
    setType(e.target.value);
  };

  const fetchMailBoxLogs = async () => {
    try {
      // const res = await fetch1(
      //   `${API_SERVICE_BACKEND}/mailboxes/getMailboxesLogs?senderEmail=${encodeURIComponent(
      //     acc?.senderEmail
      //   )}&startDate=${selectedDateRange?.from}&endDate=${
      //     selectedDateRange?.to
      //   }`
      // );
      const res = await axios.get(
        `${API_SERVICE_BACKEND}/mailboxes/getMailboxesLogs?senderEmail=${encodeURIComponent(
          acc?.senderEmail
        )}&startDate=${selectedDateRange?.from}&endDate=${
          selectedDateRange?.to
        }`
      );

      const userdata = res.data;
      const logType = validLogTypes?.[0]?.value;
      setType(logType);

      return userdata[0];
    } catch (error) {
      console.error("Error fetching logs:", error);
    }
  };
  const {
    data: logs,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: [
      "mailboxLogs",
      acc?._id,
      selectedDateRange?.from,
      selectedDateRange?.to,
    ],
    queryFn: ({ signal }) => fetchMailBoxLogs(signal),
    enabled: isDrawerOpen,
    staleTime: Infinity,
    keepPreviousData: true,
  });

  // change sort sortOrder

  useEffect(() => {
    if (!logs?.[type] || !Array.isArray(logs?.[type])) {
      setMailBox([]);
      return;
    }
    const convertToUserTimezone = (date) => {
      if (!date) return "";
      return formatToLocalDate(moment(date).tz(moment.tz.guess()).toDate());
    };

    const converted = logs?.[type]?.map((item) => ({
      ...item,
      time: convertToUserTimezone(item.time),
    }));
    setMailBox(converted);
  }, [type, logs]);

  useEffect(() => {
    if (mailBox && mailBox.length > 0) {
      const sortedMailBox = [...mailBox].sort((a, b) => {
        const dateA = new Date(a.time);
        const dateB = new Date(b.time);
        return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
      });
      setMailBox(sortedMailBox);
    }
  }, [sortOrder, logs]);
  return (
    <Box sx={{ height: "calc(100% - 100px)", p: 2 }}>
      {/* {validLogTypes?.length > 0 && */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <DateTimeRangePicker
          dateRange={dateRange}
          setFormattedDateRange={setSelectedDateRange}
          onDateRangeChange={onDateRangeChange}
        />
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
          }}
        >
          <Select
            value={sortOrder}
            onChange={handleSortOrderChange}
            size="small"
            IconComponent={(props) => (
              <ExpandMoreOutlined
                {...props}
                sx={{ color: `${theme.palette.text.primary} !important` }}
              />
            )}
            sx={{
              minWidth: "165px",
              m: "2px",
              marginLeft: "5px",
              // border: `1px solid ${selectedStatusObj.color}`,
              boxShadow: `0 1px 2px 0 ${alpha("#101828", 0.05)}`,
              "& .MuiOutlinedInput-input": {
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 1,
                "&.MuiTypography-root": {
                  width: "100%",
                },
              },
            }}
            className="glass-dropdown"
          >
            {SORTING_OPTIONS.map((option, index) => (
              <MenuItem
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "center",
                  gap: 1,
                  py: 1.5,
                  color: theme.palette.text.primary,
                }}
                value={option.value}
              >
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: "500",
                    color: (theme) => theme.palette.text.primary,
                  }}
                >
                  {option.label}
                </Typography>
              </MenuItem>
            ))}
          </Select>
          <Select
            value={type}
            onChange={handleTypeChange}
            size="small"
            IconComponent={(props) => (
              <ExpandMoreOutlined
                {...props}
                sx={{ color: `${theme.palette.text.primary} !important` }}
              />
            )}
            sx={{
              minWidth: "165px",
              m: "2px",
              // border: `1px solid ${selectedStatusObj.color}`,
              boxShadow: `0 1px 2px 0 ${alpha("#101828", 0.05)}`,
              "& .MuiOutlinedInput-input": {
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 1,
                "&.MuiTypography-root": {
                  width: "100%",
                },
              },
            }}
            className="glass-dropdown"
          >
            {validLogTypes?.map((status, index) => (
              <MenuItem
                key={index}
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "center",
                  gap: 1,
                  py: 1.5,
                  color: theme.palette.text.primary,
                }}
                value={status.value}
              >
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: "500",
                    color: (theme) => theme.palette.text.primary,
                  }}
                >
                  {status.label}
                </Typography>
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Box>

      <Box
        sx={{
          height: "calc(100% - 50px)",
          overflow: "auto",
          mt: 2,
        }}
      >
        {isFetching ? (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress />
          </Box>
        ) : mailBox && mailBox?.length > 0 ? (
          <Typography
            sx={{
              color: (theme) => theme.palette.text.primary,
              "& div, span, svg": {
                color: (theme) => `${theme.palette.text.primary} !important`,
              },
            }}
          >
            <ReactJson
              src={mailBox}
              name={type}
              enableClipboard={false}
              displayDataTypes={false}
              displayArrayKey={false}
              quotesOnKeys={false}
              displayObjectSize={false}
            />
          </Typography>
        ) : (
          <Box
            sx={{
              height: "calc(100% - 100px)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Typography sx={{ color: (theme) => theme.palette.text.primary }}>
              Not found
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default MailboxLogs;
